import { PersonLink, type PersonLinkPerson } from "@/components/common/PersonLink";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { OwnerAccessRow, PersonAdder } from "@/components/match/MatchModals";
import { ListingApplicationAccessLevel } from "@/graphql/generated/operations";
import {
  APPLICATION_LEVEL_LABELS,
  APPLICATION_LEVELS,
  APPLICATIONS_LABEL,
  applicationLevelAllowed,
  LISTING_ROLE_LABEL,
  LISTING_ROLE_LABELS,
  LISTING_ROLES,
  listingRole,
  listingRoleAxes,
  newMember,
  type ListingMemberDraft,
} from "./listing-access";

// Die Mitglieder einer Ausschreibung mit Rolle und Stufe auf Bewerbungen (listing-access.ts). Ersteller-Zeile
// und Personensuche sind dieselben Bausteine wie in den Einstellungen von Match und Projekt (OwnerAccessRow,
// PersonAdder), die Rollen tragen dieselben Namen wie im Match.
//
// Admin vergibt nur, wer `canAppointManagers` hat (der Ersteller). Ob ein Eintrag überhaupt änderbar ist, sagt
// genesis je Eintrag (`canChange`), etwa für delegierte Verwaltende die Einträge anderer Admins
// (@external-adr GENESIS-0095-managing-listing-access-is-separate-from-editing-it).
//
// Bewerbungen haben die Stufen Kein Zugriff, Lesen, Bearbeiten. Wie hoch die aufrufende Person dort vergeben darf,
// sagt `grantableApplications` aus genesis; darüber ist nur frei, was nicht über der bisherigen Stufe des Eintrags
// liegt, senken geht also immer (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers).
export function ListingMembersEditor({
  owner,
  members,
  onChange,
  canEdit,
  canAppointManagers,
  grantableApplications,
}: {
  owner: PersonLinkPerson | null;
  members: readonly ListingMemberDraft[];
  onChange: (next: ListingMemberDraft[]) => void;
  canEdit: boolean;
  canAppointManagers: boolean;
  grantableApplications: ListingApplicationAccessLevel;
}) {
  const intl = useIntl();
  const roleOptions = useMemo(
    () => (enabled: boolean) =>
      LISTING_ROLES.map((role) => ({
        value: role,
        label: intl.formatMessage(LISTING_ROLE_LABELS[role]),
        disabled: !enabled || (role === "admin" && !canAppointManagers),
      })),
    [canAppointManagers, intl],
  );
  const applicationOptions = useMemo(
    () => (enabled: boolean, member: ListingMemberDraft) =>
      APPLICATION_LEVELS.map((level) => ({
        value: level,
        label: intl.formatMessage(APPLICATION_LEVEL_LABELS[level]),
        disabled: !enabled || !applicationLevelAllowed(level, member, grantableApplications),
      })),
    [grantableApplications, intl],
  );
  const excludeIds = useMemo(
    () => [owner?.id, ...members.map((member) => member.personId)].filter((id): id is string => Boolean(id)),
    [members, owner?.id],
  );
  const roleLabel = intl.formatMessage(LISTING_ROLE_LABEL);
  const applicationsLabel = intl.formatMessage(APPLICATIONS_LABEL);

  function update(personId: string, change: Partial<ListingMemberDraft>) {
    onChange(members.map((member) => (member.personId === personId ? { ...member, ...change } : member)));
  }

  return (
    <div>
      <div className="bg-page box-flat divide-y divide-ink/5 px-4">
        {owner ? <OwnerAccessRow size="md" name={<PersonLink size="md" person={owner} />} /> : null}
        {members.map((member) => {
          const editable = canEdit && member.canChange;
          return (
            <div key={member.personId} id={`listing-member-${member.personId}`} className="py-3">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1 truncate text-smycles-body font-medium">
                  <PersonLink size="md" person={{ id: member.personId, name: member.formattedName || "–",
                    avatarContent: member.avatarContent, avatarMime: member.avatarMime }} />
                </div>
                {editable ? (
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => onChange(members.filter((m) => m.personId !== member.personId))}
                    title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
                    aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
                  >
                    ✕
                  </Button>
                ) : null}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                <div className="flex items-center gap-2">
                  <span className="smycles-field-label">{roleLabel}</span>
                  <SegmentedControl
                    id={`listing-member-${member.personId}-role`}
                    size="sm"
                    aria-label={roleLabel}
                    value={listingRole(member)}
                    onChange={(role) => update(member.personId, listingRoleAxes(role))}
                    options={roleOptions(editable)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="smycles-field-label">{applicationsLabel}</span>
                  <SegmentedControl
                    id={`listing-member-${member.personId}-applications`}
                    size="sm"
                    aria-label={applicationsLabel}
                    value={member.applications}
                    onChange={(level) => update(member.personId, { applications: level })}
                    options={applicationOptions(editable, member)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="smycles-field-meta mt-2 text-ink-muted">
        <FormattedMessage
          id="share.listings.rolesHint"
          defaultMessage="Bearbeiter:innen ändern Inhalt und Kandidaten und dürfen veröffentlichen und schließen. Admins verwalten zusätzlich Mitglieder und Rechte; zu Admins ernennt nur die Person, die die Ausschreibung angelegt hat."
        />{" "}
        <FormattedMessage
          id="share.listings.applicationsHint"
          defaultMessage="Bewerbungen sieht nur, wer dort Lesen oder Bearbeiten hat. Neue Mitglieder beginnen ohne Zugriff."
        />
        {canEdit && grantableApplications !== ListingApplicationAccessLevel.Write ? (
          <>
            {" "}
            <span id="listing-members-applications-limit">
              <FormattedMessage
                id="share.listings.applicationsLimit"
                defaultMessage="Bei Bewerbungen vergibst du höchstens deine eigene Stufe ({level}). Höhere Einträge kannst du stehen lassen oder senken."
                values={{ level: intl.formatMessage(APPLICATION_LEVEL_LABELS[grantableApplications]) }}
              />
            </span>
          </>
        ) : null}
      </p>
      {canEdit ? (
        <div className="mt-3">
          <PersonAdder
            idPrefix="listing-members"
            excludeIds={excludeIds}
            onAdd={(person) => onChange([...members, newMember(person)])}
          />
        </div>
      ) : null}
    </div>
  );
}
